<template>
  <div>
    <form-notification
      class="margin__b--24"
      :keys="error"
    />

    <input-select
      :id="id"
      :options="options"
      :placeholder="placeholder"
      :v="v"
      :clearable="clearable"
      :filterable="false"
      searchable
      v-model="address"
      @search="handleSelectSearch"
    >
      <template #selected-option>
        {{ model.to_string }}
      </template>

      <template #option="{ option } ">
        <span class="suggestion">
          <fa-icon
            class="margin__r--6 color--lg"
            :icon="['fal', 'location-dot']"
          />

          <span class="suggestion__text">
            {{ option.label }}
          </span>
        </span>
      </template>

      <template #no-options>
        <span class="d-flex justify-content-center align-items-center padding__tb--6">
          <template v-if="loader">
            <app-spinner
              class="margin__r--6"
              size="2"
            />

            {{ t('literal.loading') }}
          </template>

          <template v-else-if="q.length > 3">
            {{ t('literal.no_results_found') }}
          </template>

          <template v-else>
            {{ t('literal.type_to_find_your_location') }}
          </template>
        </span>
      </template>

      <template #list-footer>
        <span
          class="d-block padding__lr--12 padding__b--6 align--right italic color--vlg"
          v-if="options.length"
        >
          ©

          <a
            href="https://www.openstreetmap.org/copyright"
            class="link__color--vlg link--naked"
            target="_blank"
            rel="noopener"
          >
            OpenStreetMap contributors
          </a>
        </span>
      </template>
    </input-select>
  </div>
</template>

<script setup>
import { computed, ref, defineAsyncComponent } from 'vue'
import { storeToRefs } from 'pinia'
import { useLanguageStore } from '@/stores/LanguageStore'
import { useOpenStreetMapStore } from '@/stores/OpenStreetMapStore'
import { useI18n } from '@/vendors/i18n'
import { debounce as _debounce } from 'lodash'
import Address from '@/classes/addresses/Address'

import InputSelect from '&/atoms/InputSelect'
const AppSpinner = defineAsyncComponent(() => import('&/atoms/AppSpinner'))
const FormNotification = defineAsyncComponent(() => import('&/molecules/FormNotification'))

const model = defineModel({ type: Object, default: new Address() })

const { type, v } = defineProps({
  id: { type: String, required: true },
  placeholder: { type: String, default: '...' },
  type: { type: String, default: '' },
  clearable: { type: Boolean, default: true },
  v: { type: Object }
})

const { t } = useI18n()
const { getDocumentLanguage: language } = storeToRefs(useLanguageStore())
const { getSuggestions } = useOpenStreetMapStore()

const q = ref('')

const address = computed({
  get: () => model.value?.country ? model.value : null,
  set: value => model.value = new Address(value || {})
})

const query = computed(() => ({
  q: q.value,
  format: 'jsonv2',
  addressdetails: 1,
  limit: 5,
  'accept-language': language.value
}))

const { suggestions, loader, error } = getSuggestions({ query })

const options = computed(() => suggestions.value.length ? suggestions.value?.getByType(type).map(address => ({ value: address, label: address.display_name })) : [])

const handleSelectSearch = _debounce(search => {
  if (search?.length > 3) q.value = search
}, 1000)
</script>

<style scoped lang="scss">
.suggestion {
  display: flex;
  align-items: center;

  &__text {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    flex: 1 1 0;
  }
}
</style>